// @ts-nocheck
import React, { useState, useEffect } from "react";
import { Building2, Download, Upload, ShieldAlert, AlertTriangle, Check, CalendarClock, User, Copy, X, CreditCard } from "lucide-react";
import { JuridischModal } from "./JuridischeTekst";
import { useBeheerPin } from "./ui/BeheerPin";

const C = {
  bg: "#F8FAFC", bg2: "#FFFFFF", hdr: "#0F172A", hdrBdr: "#1E293B",
  txt: "#0F172A", txt2: "#475569", txt3: "#94A3B8",
  bdr: "#E2E8F0", bdr2: "#CBD5E1",
  primary: "#2563EB", primBg: "#EFF6FF", primBd: "#BFDBFE",
  green: "#16A34A", greenBg: "#DCFCE7",
  red: "#DC2626", redBg: "#FEF2F2", redBd: "#FECACA",
};

// Welke modules je kunt exporteren, en uit welke tabellen ze bestaan
const EXPORT_MODULES = [
  { id: "haccp", label: "HACCP", tabellen: ["haccp_config", "haccp_temp", "haccp_ontvangst", "haccp_terugkoel", "haccp_schoonmaak", "haccp_hygiene", "haccp_afwijking", "haccp_thermometer_calibratie"] },
  { id: "mep", label: "Mise en Place", tabellen: ["mep_data"] },
  { id: "recepten", label: "Recepten", tabellen: ["recepten_data"] },
  { id: "team", label: "Team", tabellen: ["team_data"] },
];

const inputStyle = { width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: 9, border: `1px solid ${C.bdr2}`, fontSize: 14, fontFamily: "inherit", color: C.txt };

const MODULE_LABEL = Object.fromEntries(EXPORT_MODULES.map((m) => [m.id, m.label]));
// Tabellen met één rij per tenant (blob/instellingen) — bij herstel overschrijven we die rij;
// de overige (HACCP-registraties) zijn rij-tabellen die we leegmaken en opnieuw vullen.
const SINGLETON_TABELLEN = new Set(["mep_data", "team_data", "recepten_data", "haccp_config"]);

function Kaart({ icon, titel, omschrijving, children }) {
  return (
    <div style={{ background: C.bg2, border: `1px solid ${C.bdr}`, borderRadius: 14, padding: 20, marginBottom: 18 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: omschrijving ? 4 : 14 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: C.primBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{icon}</div>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: C.txt, margin: 0 }}>{titel}</h2>
      </div>
      {omschrijving && <p style={{ fontSize: 13, color: C.txt2, margin: "0 0 16px 46px" }}>{omschrijving}</p>}
      {children}
    </div>
  );
}

export default function SettingsModule({ sb, tenantId, config, onConfigSave, supabaseClient, session, userRole }) {
  // Welk juridisch document staat open? null | "voorwaarden" | "privacy"
  const [juridischOpen, setJuridischOpen] = useState(null);
  const { pinConfigured, isUnlocked, requireBeheer, wijzigPin, disablePin, lockNow } = useBeheerPin();
  // KC_UI_V2_SETTINGS_PIN_GUARDS
  const [beheerPin1, setBeheerPin1] = useState("");
  const [beheerPin2, setBeheerPin2] = useState("");
  const [beheerPinStatus, setBeheerPinStatus] = useState("");
  const [beheerPinFout, setBeheerPinFout] = useState("");

  const bewaarBeheerPin = async () => {
    setBeheerPinFout("");
    setBeheerPinStatus("bezig");
    const r = await wijzigPin(beheerPin1, beheerPin2);
    setBeheerPinStatus("");
    if (!r?.ok) { setBeheerPinFout(r?.fout || "Pincode opslaan lukte niet."); return; }
    setBeheerPin1(""); setBeheerPin2("");
    setBeheerPinStatus("klaar");
    setTimeout(() => setBeheerPinStatus(""), 2500);
  };

  const zetBeheerOpSlot = () => { lockNow(); };
  const ontgrendelBeheer = async () => { await requireBeheer("beheer te ontgrendelen"); };
  const schakelBeheerPinUit = async () => {
    const ok = await disablePin();
    if (ok) { setBeheerPin1(""); setBeheerPin2(""); }
  };


  // ── Account (eigen gegevens + wachtwoord) ──────────────────────────────
  const [nwPw, setNwPw] = useState("");
  const [nwPw2, setNwPw2] = useState("");
  const [pwStatus, setPwStatus] = useState(""); // "" | "bezig" | "klaar"
  const [pwFout, setPwFout] = useState("");

  // Schermnaam (roepnaam) — opgeslagen in Supabase Auth user-metadata
  const [roepnaam, setRoepnaam] = useState(session?.user?.user_metadata?.roepnaam || "");
  const [naamStatus, setNaamStatus] = useState(""); // "" | "bezig" | "klaar"
  const [naamFout, setNaamFout] = useState("");

  const rolLabel = (r) => ({ owner: "Beheerder", manager: "Manager", medewerker: "Medewerker", staff: "Medewerker" }[r] || (r ? r.charAt(0).toUpperCase() + r.slice(1) : "—"));

  const wijzigWachtwoord = async () => {
    if (!(await requireBeheer("het accountwachtwoord te wijzigen"))) return;
    setPwFout("");
    if (nwPw.length < 8) { setPwFout("Gebruik minstens 8 tekens."); return; }
    if (nwPw !== nwPw2) { setPwFout("De wachtwoorden komen niet overeen."); return; }
    if (!supabaseClient) { setPwFout("Geen verbinding met de server."); return; }
    setPwStatus("bezig");
    const { error } = await supabaseClient.auth.updateUser({ password: nwPw });
    if (error) { setPwStatus(""); setPwFout(error.message); return; }
    setPwStatus("klaar");
    setNwPw(""); setNwPw2("");
    setTimeout(() => setPwStatus(""), 2500);
  };

  const bewaarRoepnaam = async () => {
    if (!(await requireBeheer("de schermnaam van het account te wijzigen"))) return;
    setNaamFout("");
    if (!supabaseClient) { setNaamFout("Geen verbinding met de server."); return; }
    setNaamStatus("bezig");
    const { error } = await supabaseClient.auth.updateUser({ data: { roepnaam: roepnaam.trim() } });
    if (error) { setNaamStatus(""); setNaamFout(error.message); return; }
    setNaamStatus("klaar");
    setTimeout(() => setNaamStatus(""), 2500);
  };

  // ── Gebruikers / uitnodigingen (alleen owner) ──────────────────────────
  const isOwner = userRole === "owner";
  const isBeheer = userRole === "owner" || userRole === "manager";

  // ── Abonnement (alleen owner) ──────────────────────────────────────────
  const [opzegBevestigen, setOpzegBevestigen] = useState(false);
  const [opzegBezig, setOpzegBezig] = useState(false);
  const [opzegFout, setOpzegFout] = useState("");
  const [opzegKlaar, setOpzegKlaar] = useState("");

  const abonnementStatus = String(config?.subscription_status || "").toLowerCase();
  const abonnementPlan = config?.subscription_plan === "yearly" ? "Jaarlijks" : config?.subscription_plan === "monthly" ? "Maandelijks" : "—";
  const abonnementPrijs = config?.subscription_plan === "yearly" ? "€ 385 excl. btw per jaar" : config?.subscription_plan === "monthly" ? "€ 35 excl. btw per maand" : "";
  const abonnementOpzegbaar = abonnementStatus === "active" && Boolean(config?.mollie_subscription_id);

  const datumNl = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return new Intl.DateTimeFormat("nl-NL", { day: "numeric", month: "long", year: "numeric" }).format(d);
  };

  const zegAbonnementOp = async () => {
    if (!abonnementOpzegbaar || opzegBezig) return;
    if (!(await requireBeheer("het KitchenControl-abonnement op te zeggen"))) return;

    setOpzegBezig(true);
    setOpzegFout("");
    setOpzegKlaar("");
    try {
      const response = await fetch("/.netlify/functions/cancel-mollie-subscription", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          authorization: `Bearer ${session?.access_token || ""}`,
        },
        body: JSON.stringify({ tenant_id: tenantId }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setOpzegFout(data?.error || "Abonnement opzeggen is mislukt.");
        return;
      }

      const tot = datumNl(data?.access_until);
      setOpzegKlaar(tot
        ? `Het abonnement is opgezegd. Je kunt KitchenControl blijven gebruiken tot en met ${tot}.`
        : "Het abonnement is opgezegd.");
      setOpzegBevestigen(false);
      window.setTimeout(() => window.location.reload(), 1800);
    } catch {
      setOpzegFout("Abonnement opzeggen is mislukt.");
    } finally {
      setOpzegBezig(false);
    }
  };
  const [invites, setInvites] = useState([]);
  const [invEmail, setInvEmail] = useState("");
  const [invVoornaam, setInvVoornaam] = useState("");
  const [invBezig, setInvBezig] = useState(false);
  const [invFout, setInvFout] = useState("");
  const [invOk, setInvOk] = useState("");

  const knopPrimary = { background: C.primary, color: "#fff", border: "none", borderRadius: 9, padding: "10px 16px", fontSize: 13.5, fontWeight: 600, fontFamily: "inherit", cursor: "pointer" };
  const knopSecondary = { background: C.bg2, color: C.txt2, border: `1px solid ${C.bdr2}`, borderRadius: 8, padding: "7px 11px", fontSize: 12.5, fontWeight: 600, fontFamily: "inherit", cursor: "pointer", whiteSpace: "nowrap" };

  // Openstaande uitnodigingen laden via de beveiligde functie (owner-only)
  const laadUitnodigingen = async () => {
    if (!isOwner || !supabaseClient || !tenantId) return;
    const { data, error } = await supabaseClient.rpc("tenant_uitnodigingen", { p_tenant_id: tenantId });
    if (!error) setInvites(data || []);
  };

  useEffect(() => { laadUitnodigingen(); }, [isOwner, supabaseClient, tenantId]);

  // Collega toevoegen + meteen een magic-link-uitnodiging mailen
  const voegCollegaToe = async () => {
    if (!(await requireBeheer("een persoonlijk account uit te nodigen"))) return;
    setInvFout(""); setInvOk("");
    const email = invEmail.trim().toLowerCase();
    const voornaam = invVoornaam.trim();
    if (!voornaam) { setInvFout("Vul een voornaam in."); return; }
    if (!email) { setInvFout("Vul een e-mailadres in."); return; }
    if (!supabaseClient || !tenantId) { setInvFout("Geen verbinding met de server."); return; }
    setInvBezig(true);

    // 1) Uitnodiging klaarzetten in de database (owner-check zit in de functie)
    const { error: invErr } = await supabaseClient.rpc("nodig_collega_uit", {
      p_tenant_id: tenantId, p_email: email, p_voornaam: voornaam, p_role: "manager",
    });
    if (invErr) {
      setInvBezig(false);
      setInvFout(invErr.message || "Uitnodiging klaarzetten lukte niet.");
      return;
    }

    // 2) Magic link mailen. shouldCreateUser:true want de collega heeft nog geen
    //    account; dit gebeurt gecontroleerd door een ingelogde owner.
    const { error: otpErr } = await supabaseClient.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: true,
        emailRedirectTo: `${window.location.origin}/`,
        data: { roepnaam: voornaam },
      },
    });
    setInvBezig(false);
    if (otpErr) {
      // Uitnodiging staat klaar, alleen de mail mislukte. De collega kan ook via
      // "stuur inloglink" op het inlogscherm alsnog binnenkomen.
      setInvFout("Uitnodiging staat klaar, maar de inlogmail versturen lukte niet: " + otpErr.message);
      laadUitnodigingen();
      return;
    }

    setInvEmail(""); setInvVoornaam("");
    setInvOk(`${voornaam} is uitgenodigd — er is een inloglink gemaild naar ${email}.`);
    setTimeout(() => setInvOk(""), 6000);
    laadUitnodigingen();
  };

  const trekIn = async (id) => {
    if (!(await requireBeheer("een uitnodiging in te trekken"))) return;
    if (!supabaseClient) return;
    const { error } = await supabaseClient.rpc("trek_uitnodiging_in", { p_id: id });
    if (!error) setInvites((lijst) => lijst.filter((i) => i.id !== id));
  };

  const opnieuwMailen = async (email, voornaam) => {
    if (!(await requireBeheer("een nieuwe inloglink te versturen"))) return;
    if (!supabaseClient) return;
    const { error } = await supabaseClient.auth.signInWithOtp({
      email,
      options: { shouldCreateUser: true, emailRedirectTo: `${window.location.origin}/`, data: { roepnaam: voornaam || "" } },
    });
    if (error) { setInvFout("Opnieuw mailen lukte niet: " + error.message); }
    else { setInvOk(`Nieuwe inloglink gemaild naar ${email}.`); setTimeout(() => setInvOk(""), 5000); }
  };

  // ── Huidige gebruikers (ledenlijst + toegang intrekken) ──────────────
  const [leden, setLeden] = useState([]);
  const [ledenFout, setLedenFout] = useState("");

  const laadLeden = async () => {
    if (!isOwner || !supabaseClient || !tenantId) return;
    const { data, error } = await supabaseClient.rpc("tenant_leden", { t: tenantId });
    if (error) { setLedenFout("Leden ophalen lukte niet: " + error.message); return; }
    setLedenFout("");
    setLeden(data || []);
  };

  useEffect(() => { laadLeden(); }, [isOwner, supabaseClient, tenantId]);

  const verwijderLid = async (userId, email) => {
    if (!(await requireBeheer("toegang van een gebruiker in te trekken"))) return;
    if (!supabaseClient || !tenantId) return;
    if (!confirm(`Toegang van ${email || "deze gebruiker"} intrekken? Hij kan dan niet meer inloggen bij dit bedrijf.`)) return;
    const { error } = await supabaseClient.rpc("verwijder_lid", { t: tenantId, doel: userId });
    if (error) { alert("Intrekken lukte niet: " + error.message); return; }
    setLeden((lijst) => lijst.filter((l) => l.user_id !== userId));
  };

  // ── Bedrijf ────────────────────────────────────────────────────────────
  const [naam, setNaam] = useState(config?.bedrijfsnaam || "");
  const [logo, setLogo] = useState(config?.logo_url || "");
  const [plaats, setPlaats] = useState(config?.weer_plaats || "");
  const [bedrijfStatus, setBedrijfStatus] = useState("");

  const bewaarBedrijf = async () => {
    if (!(await requireBeheer("bedrijfsinstellingen te wijzigen"))) return;
    setBedrijfStatus("bezig");
    await onConfigSave({ ...config, bedrijfsnaam: naam.trim(), logo_url: logo.trim(), weer_plaats: plaats.trim() });
    setBedrijfStatus("klaar");
    setTimeout(() => setBedrijfStatus(""), 2500);
  };

  // ── Houdbaarheid (THT-norm) ────────────────────────────────────────────
  const [thtNorm, setThtNorm] = useState(config?.tht_norm_dagen ?? 3);
  const [thtStatus, setThtStatus] = useState("");

  const bewaarTht = async () => {
    if (!(await requireBeheer("de houdbaarheidsnorm te wijzigen"))) return;
    const dagen = Math.max(1, parseInt(thtNorm, 10) || 3);
    setThtNorm(dagen);
    setThtStatus("bezig");
    await onConfigSave({ ...config, tht_norm_dagen: dagen });
    setThtStatus("klaar");
    setTimeout(() => setThtStatus(""), 2500);
  };

  // ── Back-up ────────────────────────────────────────────────────────────
  const [gekozen, setGekozen] = useState({ haccp: true, mep: true, recepten: true, team: true });
  const [bezigExport, setBezigExport] = useState(false);
  const ietsGekozen = Object.values(gekozen).some(Boolean);

  // Import-state
  const [importBestand, setImportBestand] = useState(null); // { data, naam }
  const [importFout, setImportFout] = useState("");
  const [importKlaar, setImportKlaar] = useState("");
  const [bezigImport, setBezigImport] = useState(false);

  // Verzamel een back-up-object van de opgegeven modules
  const verzamelBackup = async (moduleIds) => {
    const data = {
      app: "KitchenControl", versie: 1, tenant_id: tenantId,
      bedrijfsnaam: config?.bedrijfsnaam || "",
      geexporteerd_op: new Date().toISOString(), modules: {},
    };
    for (const m of EXPORT_MODULES.filter((x) => moduleIds.includes(x.id))) {
      data.modules[m.id] = {};
      for (const t of m.tabellen) {
        const rows = await sb.select(t, `tenant_id=eq.${tenantId}&select=*`);
        data.modules[m.id][t] = rows || [];
      }
    }
    return data;
  };

  // Download een object als JSON-bestand
  const downloadJson = (obj, suffix = "") => {
    const blob = new Blob([JSON.stringify(obj, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const naamDeel = (config?.bedrijfsnaam || "kitchencontrol").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "kitchencontrol";
    const datum = new Date().toISOString().slice(0, 10);
    const a = document.createElement("a");
    a.href = url;
    a.download = `kitchencontrol-backup-${suffix ? suffix + "-" : ""}${naamDeel}-${datum}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const exporteer = async () => {
    if (!ietsGekozen || bezigExport) return;
    setBezigExport(true);
    try {
      const ids = EXPORT_MODULES.filter((x) => gekozen[x.id]).map((x) => x.id);
      const data = await verzamelBackup(ids);
      downloadJson(data, "");
    } catch (e) {
      console.warn("[Instellingen] export mislukt:", e);
    } finally {
      setBezigExport(false);
    }
  };

  // Bestand kiezen voor import
  const kiesBestand = (e) => {
    setImportFout(""); setImportKlaar(""); setImportBestand(null);
    const file = e.target.files?.[0];
    e.target.value = ""; // reset zodat hetzelfde bestand opnieuw gekozen kan worden
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result);
        if (parsed?.app !== "KitchenControl" || !parsed?.modules || typeof parsed.modules !== "object") {
          setImportFout("Dit lijkt geen KitchenControl-back-upbestand te zijn.");
          return;
        }
        const ids = Object.keys(parsed.modules).filter((id) => EXPORT_MODULES.some((m) => m.id === id));
        if (ids.length === 0) {
          setImportFout("Het bestand bevat geen herkenbare modules om terug te zetten.");
          return;
        }
        setImportBestand({ data: parsed, naam: file.name, moduleIds: ids });
      } catch {
        setImportFout("Kon het bestand niet lezen (ongeldige JSON).");
      }
    };
    reader.readAsText(file);
  };

  // Eén tabel vervangen vanuit de back-up
  const herstelTabel = async (table, rows) => {
    rows = rows || [];
    if (SINGLETON_TABELLEN.has(table)) {
      const src = rows[0];
      if (!src) return; // bestand bevat deze tabel niet → bestaande rij met rust laten
      const { id, tenant_id, ...rest } = src;
      const body = { ...rest, tenant_id: tenantId };
      const bestaand = await sb.select(table, `tenant_id=eq.${tenantId}&select=tenant_id&limit=1`);
      if (bestaand.length) await sb.update(table, `tenant_id=eq.${tenantId}`, body);
      else await sb.insert(table, body);
    } else {
      await sb.remove(table, `tenant_id=eq.${tenantId}`);
      if (rows.length) {
        const reScoped = rows.map((r) => { const { id, tenant_id, ...rest } = r; return { ...rest, tenant_id: tenantId }; });
        await sb.insert(table, reScoped);
      }
    }
  };

  const voerImportUit = async () => {
    if (!(await requireBeheer("een back-up terug te zetten"))) return;
    if (!importBestand || bezigImport) return;
    setBezigImport(true);
    setImportFout(""); setImportKlaar("");
    try {
      const backup = importBestand.data;
      const moduleIds = importBestand.moduleIds;

      // 1) Veiligheids-back-up van de HUIDIGE gegevens (vóór overschrijven)
      const huidig = await verzamelBackup(moduleIds);
      downloadJson(huidig, "VOOR-HERSTEL");

      // 2) Per module/tabel vervangen
      for (const mid of moduleIds) {
        const tabellen = backup.modules[mid] || {};
        for (const [table, rows] of Object.entries(tabellen)) {
          await herstelTabel(table, rows);
        }
      }
      setImportBestand(null);
      setImportKlaar("✓ Back-up teruggezet. Herlaad de app om alle gegevens bijgewerkt te zien.");
    } catch (e) {
      console.warn("[Instellingen] import mislukt:", e);
      setImportFout("Er ging iets mis tijdens het terugzetten. De veiligheids-back-up van je vorige gegevens is gedownload, zodat je niets kwijt bent.");
    } finally {
      setBezigImport(false);
    }
  };

  const knopStyle = (uit) => ({ display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 18px", borderRadius: 9, border: "none", background: uit ? C.bdr : C.primary, color: uit ? C.txt3 : "#fff", fontSize: 14, fontWeight: 700, fontFamily: "inherit", cursor: uit ? "not-allowed" : "pointer" });

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: C.bg }}>
      {/* Header */}
      <header style={{ background: C.hdr, borderBottom: `1px solid ${C.hdrBdr}`, flexShrink: 0, height: 52, display: "flex", alignItems: "center", padding: "0 18px" }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: "#fff", letterSpacing: 0.3 }}>⚙️ Instellingen</span>
      </header>

      {/* Inhoud */}
      <div style={{ flex: 1, overflowY: "auto", padding: "clamp(20px, 4vw, 36px)" }}>
        <div style={{ maxWidth: 720 }}>

          {/* ── Beheer-pincode ── */}
          {isOwner && (
            <Kaart icon={<ShieldAlert size={18} color={C.primary} />} titel="Beheer-pincode"
              omschrijving="Bescherm wijzigingen en verwijderingen op een gedeelde keukentablet met een 4-cijferige pincode.">
              <div style={{ marginLeft: 46 }}>
                <div style={{ padding: "11px 13px", background: pinConfigured ? C.greenBg : C.primBg, border: `1px solid ${pinConfigured ? "#BBF7D0" : C.primBd}`, borderRadius: 10, marginBottom: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.txt }}>
                    {pinConfigured ? "Beheer-slot actief" : "Nog geen beheer-pincode ingesteld"}
                  </div>
                  <div style={{ fontSize: 12, color: C.txt2, lineHeight: 1.5, marginTop: 3 }}>
                    {pinConfigured
                      ? (isUnlocked ? "Beheer is nu ontgrendeld. Het slot wordt automatisch weer actief na 10 minuten." : "Dagelijks gebruik blijft vrij. Voor beheerwijzigingen wordt de pincode gevraagd.")
                      : "Zolang je geen pincode instelt, blijft KitchenControl werken zoals nu en kan iedereen met deze login ook beheerwijzigingen doen."}
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "minmax(120px,180px) minmax(120px,180px)", gap: 10, marginBottom: 10 }}>
                  <div>
                    <label style={{ display: "block", fontSize: 12, color: C.txt2, fontWeight: 600, marginBottom: 5 }}>{pinConfigured ? "Nieuwe pincode" : "Kies pincode"}</label>
                    <input inputMode="numeric" pattern="[0-9]*" maxLength={4} style={inputStyle} value={beheerPin1} onChange={(e) => setBeheerPin1(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="4 cijfers" />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: 12, color: C.txt2, fontWeight: 600, marginBottom: 5 }}>Herhaal pincode</label>
                    <input inputMode="numeric" pattern="[0-9]*" maxLength={4} style={inputStyle} value={beheerPin2} onChange={(e) => setBeheerPin2(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="4 cijfers" />
                  </div>
                </div>
                {beheerPinFout && <div style={{ color: C.red, fontSize: 12.5, marginBottom: 10 }}>{beheerPinFout}</div>}
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <button onClick={bewaarBeheerPin} disabled={beheerPinStatus === "bezig"} style={{ ...knopPrimary, opacity: beheerPinStatus === "bezig" ? .7 : 1 }}>
                    {beheerPinStatus === "bezig" ? "Bezig…" : pinConfigured ? "Pincode wijzigen" : "Pincode instellen"}
                  </button>
                  {pinConfigured && !isUnlocked && <button onClick={ontgrendelBeheer} style={knopSecondary}>Beheer ontgrendelen</button>}
                  {pinConfigured && isUnlocked && <button onClick={zetBeheerOpSlot} style={knopSecondary}>Nu vergrendelen</button>}
                  {pinConfigured && <button onClick={schakelBeheerPinUit} style={{ ...knopSecondary, color: C.red, borderColor: C.redBd }}>Pincode uitschakelen</button>}
                  {beheerPinStatus === "klaar" && <span style={{ color: C.green, fontSize: 12.5, fontWeight: 600 }}>Opgeslagen</span>}
                </div>
                <p style={{ fontSize: 11.5, color: C.txt3, lineHeight: 1.5, margin: "12px 0 0" }}>
                  De pincode wordt niet leesbaar opgeslagen. Na een juiste pincode blijft beheer 10 minuten ontgrendeld.
                </p>
              </div>
            </Kaart>
          )}

          {/* ── Account ── */}
          <Kaart icon={<User size={18} color={C.primary} />} titel="Account"
            omschrijving="Je eigen inloggegevens en wachtwoord.">
            <div style={{ marginLeft: 46 }}>
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "9px 0", borderBottom: `1px solid ${C.bdr}` }}>
                  <span style={{ fontSize: 12.5, color: C.txt3, fontWeight: 600 }}>E-mailadres</span>
                  <span style={{ fontSize: 13.5, color: C.txt, fontWeight: 600, textAlign: "right", wordBreak: "break-word" }}>{session?.user?.email || "—"}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "9px 0", borderBottom: `1px solid ${C.bdr}` }}>
                  <span style={{ fontSize: 12.5, color: C.txt3, fontWeight: 600 }}>Rol</span>
                  <span style={{ fontSize: 13.5, color: C.txt, fontWeight: 600, textAlign: "right" }}>{rolLabel(userRole)}</span>
                </div>
                {config?.bedrijfsnaam ? (
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "9px 0", borderBottom: `1px solid ${C.bdr}` }}>
                    <span style={{ fontSize: 12.5, color: C.txt3, fontWeight: 600 }}>Bedrijf</span>
                    <span style={{ fontSize: 13.5, color: C.txt, fontWeight: 600, textAlign: "right" }}>{config.bedrijfsnaam}</span>
                  </div>
                ) : null}
              </div>

              {/* Schermnaam (roepnaam) */}
              <label style={{ display: "block", fontSize: 12, color: C.txt2, fontWeight: 600, marginBottom: 5 }}>Schermnaam (roepnaam)</label>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
                <input style={{ ...inputStyle, maxWidth: 320 }} value={roepnaam} maxLength={40} placeholder="bijv. Hans"
                  onChange={(e) => setRoepnaam(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") bewaarRoepnaam(); }} />
                <button onClick={bewaarRoepnaam} disabled={naamStatus === "bezig"} style={{ background: C.primary, color: "#fff", border: "none", borderRadius: 9, padding: "10px 16px", fontSize: 13.5, fontWeight: 600, fontFamily: "inherit", cursor: naamStatus === "bezig" ? "default" : "pointer", opacity: naamStatus === "bezig" ? 0.7 : 1 }}>
                  {naamStatus === "bezig" ? "Bezig…" : "Opslaan"}
                </button>
                {naamStatus === "klaar" && <span style={{ color: C.green, fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", gap: 5 }}><Check size={15} /> Opgeslagen</span>}
              </div>
              {naamFout && <div style={{ color: "#DC2626", fontSize: 12.5, marginBottom: 8 }}>{naamFout}</div>}
              <p style={{ fontSize: 11.5, color: C.txt3, margin: "0 0 18px" }}>Wordt op het beginscherm getoond in plaats van je e-mailadres.</p>

              <label style={{ display: "block", fontSize: 12, color: C.txt2, fontWeight: 600, marginBottom: 5 }}>Nieuw wachtwoord</label>
              <input type="password" style={{ ...inputStyle, maxWidth: 320, marginBottom: 10 }} value={nwPw} onChange={(e) => setNwPw(e.target.value)} placeholder="minstens 8 tekens" />
              <label style={{ display: "block", fontSize: 12, color: C.txt2, fontWeight: 600, marginBottom: 5 }}>Herhaal wachtwoord</label>
              <input type="password" style={{ ...inputStyle, maxWidth: 320 }} value={nwPw2} onChange={(e) => setNwPw2(e.target.value)} placeholder="herhaal wachtwoord" />
              {pwFout && <div style={{ color: "#DC2626", fontSize: 12.5, marginTop: 8 }}>{pwFout}</div>}
              <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 12 }}>
                <button onClick={wijzigWachtwoord} disabled={pwStatus === "bezig"} style={{ background: C.primary, color: "#fff", border: "none", borderRadius: 9, padding: "10px 16px", fontSize: 13.5, fontWeight: 600, fontFamily: "inherit", cursor: pwStatus === "bezig" ? "default" : "pointer", opacity: pwStatus === "bezig" ? 0.7 : 1 }}>
                  {pwStatus === "bezig" ? "Bezig…" : "Wachtwoord wijzigen"}
                </button>
                {pwStatus === "klaar" && <span style={{ color: C.green, fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", gap: 5 }}><Check size={15} /> Opgeslagen</span>}
              </div>
            </div>
          </Kaart>

          {/* ── Gebruikers (owner) ── */}
          {isOwner && (
            <Kaart icon={<User size={18} color={C.primary} />} titel="Gebruikers & toegang"
              omschrijving="Persoonlijke accounts zijn optioneel. Op een gedeelde keukentablet kan één vaste inlog worden gebruikt.">
              <div style={{ marginLeft: 46 }}>
                {/* KC_UI_V2_SETTINGS_USERS */}
                <div style={{ padding: "12px 14px", background: C.primBg, border: `1px solid ${C.primBd}`, borderRadius: 10, marginBottom: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.txt, marginBottom: 4 }}>Medewerkers zijn iets anders dan gebruikers</div>
                  <div style={{ fontSize: 12.5, color: C.txt2, lineHeight: 1.55 }}>
                    Medewerkers beheer je onder <strong>Team</strong>. Zij kunnen worden gekozen bij etiketten, HACCP-registraties en andere handelingen zonder dat zij een eigen KitchenControl-account nodig hebben.
                  </div>
                </div>

                <details style={{ border: `1px solid ${C.bdr}`, borderRadius: 10, background: C.bg, marginBottom: 18 }}>
                  <summary style={{ cursor: "pointer", padding: "12px 14px", fontSize: 13, fontWeight: 700, color: C.primary, listStylePosition: "inside" }}>
                    Persoonlijk account uitnodigen (optioneel)
                  </summary>
                  <div style={{ padding: "0 14px 14px" }}>
                    <p style={{ fontSize: 12.5, color: C.txt2, lineHeight: 1.5, margin: "0 0 12px" }}>
                      Handig voor bijvoorbeeld een eigenaar, manager of chef-kok die zelfstandig wil inloggen. Voor dagelijks gebruik op een gedeelde tablet is dit niet nodig.
                    </p>
                    <div style={{ padding: "10px 12px", background: C.bg2, border: `1px solid ${C.bdr}`, borderRadius: 9, marginBottom: 14 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 700, color: C.txt, marginBottom: 5 }}>Gedeelde login en rechten</div>
                      <div style={{ fontSize: 12, color: C.txt2, lineHeight: 1.5 }}>
                        Iedereen die dezelfde login gebruikt heeft technisch dezelfde accountrechten. Daarom kan een <strong>beheer-pincode</strong> worden ingesteld voor wijzigingen en verwijderingen. Dagelijkse handelingen blijven zonder pincode beschikbaar.
                      </div>
                      <div style={{ fontSize: 11.5, color: C.txt3, lineHeight: 1.45, marginTop: 7 }}>
                        Een persoonlijk account dat je hier uitnodigt krijgt manager-toegang. Gewone medewerkers hoeven geen persoonlijk account te hebben.
                      </div>
                    </div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 14 }}>
                  <div style={{ flex: "1 1 150px" }}>
                    <label style={{ display: "block", fontSize: 12, color: C.txt2, fontWeight: 600, marginBottom: 5 }}>Voornaam</label>
                    <input style={inputStyle} value={invVoornaam} onChange={(e) => setInvVoornaam(e.target.value)} placeholder="bijv. Peter" />
                  </div>
                  <div style={{ flex: "1 1 220px" }}>
                    <label style={{ display: "block", fontSize: 12, color: C.txt2, fontWeight: 600, marginBottom: 5 }}>E-mailadres collega</label>
                    <input type="email" style={inputStyle} value={invEmail} onChange={(e) => setInvEmail(e.target.value)} placeholder="collega@bedrijf.nl" />
                  </div>
                  <button onClick={voegCollegaToe} disabled={invBezig} style={{ ...knopPrimary, opacity: invBezig ? 0.7 : 1 }}>
                    {invBezig ? "Bezig…" : "Toevoegen & uitnodigen"}
                  </button>
                </div>
                {invFout && <div style={{ color: "#DC2626", fontSize: 12.5, marginBottom: 10 }}>{invFout}</div>}
                {invOk && <div style={{ color: C.green, fontSize: 12.5, marginBottom: 10, fontWeight: 600 }}>{invOk}</div>}

                {invites.length === 0 ? (
                  <p style={{ fontSize: 13, color: C.txt3, margin: 0 }}>Nog geen openstaande uitnodigingen.</p>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {invites.map((inv) => (
                      <div key={inv.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", border: `1px solid ${C.bdr}`, borderRadius: 9, background: C.bg }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13.5, fontWeight: 600, color: C.txt, wordBreak: "break-word" }}>{inv.voornaam ? inv.voornaam + " · " : ""}{inv.email || "—"}</div>
                          <div style={{ fontSize: 11.5, color: C.txt3 }}>{rolLabel(inv.role)} · uitgenodigd</div>
                        </div>
                        <button onClick={() => opnieuwMailen(inv.email, inv.voornaam)} style={knopSecondary}>Opnieuw mailen</button>
                        <button onClick={() => trekIn(inv.id)} style={{ ...knopSecondary, color: "#DC2626", borderColor: "#FECACA", background: "#FEF2F2" }}>Intrekken</button>
                      </div>
                    ))}
                  </div>
                )}
                <p style={{ fontSize: 12, color: C.txt3, margin: "12px 0 0", lineHeight: 1.5 }}>
                  De collega krijgt een mail met een inloglink. Na één klik is hij of zij ingelogd en gekoppeld aan dit bedrijf. Een wachtwoord instellen kan later, vrijblijvend, onder Account.
                </p>

                  </div>
                </details>

                {/* Huidige gebruikers */}
                <div>
                  <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10, marginBottom: 10 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.txt }}>Persoonlijke accounts</div>
                    <div style={{ fontSize: 11.5, color: C.txt3 }}>{leden.length} actief</div>
                  </div>
                  {ledenFout && <div style={{ color: "#DC2626", fontSize: 12.5, marginBottom: 10 }}>{ledenFout}</div>}
                  {leden.length === 0 ? (
                    <p style={{ fontSize: 13, color: C.txt3, margin: 0 }}>Nog geen gebruikers geladen.</p>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {leden.map((lid) => {
                        const ikZelf = lid.user_id === session?.user?.id;
                        const isBeheerder = lid.role === "owner";
                        return (
                          <div key={lid.user_id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", border: `1px solid ${C.bdr}`, borderRadius: 9, background: C.bg }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13.5, fontWeight: 600, color: C.txt, wordBreak: "break-word" }}>{lid.email || "—"}{ikZelf ? " (jij)" : ""}</div>
                              <div style={{ fontSize: 11.5, color: C.txt3 }}>{rolLabel(lid.role)}</div>
                            </div>
                            {(!ikZelf && !isBeheerder) && (
                              <button onClick={() => verwijderLid(lid.user_id, lid.email)} style={{ ...knopSecondary, color: "#DC2626", borderColor: "#FECACA", background: "#FEF2F2" }}>Toegang intrekken</button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </Kaart>
          )}

          {isOwner && (
            <Kaart
              icon={<CreditCard size={18} color={C.primary} />}
              titel="Abonnement"
              omschrijving="Bekijk je KitchenControl-abonnement en beheer de automatische verlenging."
            >
              <div style={{ marginLeft: 46 }}>
                <div style={{ border: `1px solid ${C.bdr}`, borderRadius: 10, overflow: "hidden", marginBottom: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "10px 12px", borderBottom: `1px solid ${C.bdr}` }}>
                    <span style={{ fontSize: 12.5, color: C.txt3, fontWeight: 600 }}>Abonnement</span>
                    <span style={{ fontSize: 13.5, color: C.txt, fontWeight: 700, textAlign: "right" }}>{abonnementPlan}</span>
                  </div>
                  {abonnementPrijs && (
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "10px 12px", borderBottom: `1px solid ${C.bdr}` }}>
                      <span style={{ fontSize: 12.5, color: C.txt3, fontWeight: 600 }}>Prijs</span>
                      <span style={{ fontSize: 13.5, color: C.txt, fontWeight: 600, textAlign: "right" }}>{abonnementPrijs}</span>
                    </div>
                  )}
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "10px 12px" }}>
                    <span style={{ fontSize: 12.5, color: C.txt3, fontWeight: 600 }}>Status</span>
                    <span style={{ fontSize: 13.5, color: abonnementStatus === "active" ? C.green : abonnementStatus === "cancelled" ? C.red : C.txt2, fontWeight: 700, textAlign: "right" }}>
                      {abonnementStatus === "active"
                        ? "Actief"
                        : abonnementStatus === "cancelled"
                          ? `Opgezegd${config?.subscription_access_until ? ` · toegang t/m ${datumNl(config.subscription_access_until)}` : ""}`
                          : abonnementStatus === "internal"
                            ? "Intern"
                            : abonnementStatus || "Onbekend"}
                    </span>
                  </div>
                </div>

                {abonnementOpzegbaar && !opzegBevestigen && (
                  <>
                    <p style={{ fontSize: 12.5, lineHeight: 1.55, color: C.txt2, margin: "0 0 12px" }}>
                      Bij opzeggen stopt de automatische verlenging bij Mollie. Je huidige betaalde periode blijft beschikbaar tot de einddatum.
                    </p>
                    <button
                      type="button"
                      onClick={() => { setOpzegBevestigen(true); setOpzegFout(""); }}
                      style={{ ...knopSecondary, color: C.red, borderColor: C.redBd, background: C.redBg }}
                    >
                      Abonnement opzeggen
                    </button>
                  </>
                )}

                {abonnementOpzegbaar && opzegBevestigen && (
                  <div style={{ padding: 14, borderRadius: 10, border: `1px solid ${C.redBd}`, background: C.redBg }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: C.txt, marginBottom: 6 }}>Weet je zeker dat je wilt opzeggen?</div>
                    <div style={{ fontSize: 12.5, lineHeight: 1.55, color: C.txt2, marginBottom: 12 }}>
                      Mollie maakt daarna geen nieuwe automatische incasso's meer aan. Je toegang blijft actief tot het einde van de reeds betaalde periode.
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button type="button" disabled={opzegBezig} onClick={zegAbonnementOp}
                        style={{ ...knopSecondary, color: "#fff", borderColor: C.red, background: C.red, opacity: opzegBezig ? .65 : 1 }}>
                        {opzegBezig ? "Opzeggen…" : "Ja, abonnement opzeggen"}
                      </button>
                      <button type="button" disabled={opzegBezig} onClick={() => setOpzegBevestigen(false)} style={knopSecondary}>
                        Niet opzeggen
                      </button>
                    </div>
                  </div>
                )}

                {opzegFout && <div style={{ marginTop: 10, color: C.red, fontSize: 12.5, fontWeight: 600 }}>{opzegFout}</div>}
                {opzegKlaar && <div style={{ marginTop: 10, color: C.green, fontSize: 12.5, fontWeight: 600 }}>{opzegKlaar}</div>}
              </div>
            </Kaart>
          )}

          {isBeheer && (<>
          {/* ── Bedrijf ── */}
          <Kaart icon={<Building2 size={18} color={C.primary} />} titel="Bedrijf"
            omschrijving="Naam en logo van je bedrijf. Verschijnt in de zijbalk, op het beginscherm en op rapporten.">
            <div style={{ marginLeft: 46 }}>
              <label style={{ display: "block", fontSize: 12, color: C.txt2, fontWeight: 600, marginBottom: 5 }}>Bedrijfsnaam</label>
              <input style={{ ...inputStyle, marginBottom: 14 }} value={naam} onChange={(e) => setNaam(e.target.value)} placeholder="Bijv. BijHENC" />

              <label style={{ display: "block", fontSize: 12, color: C.txt2, fontWeight: 600, marginBottom: 5 }}>Logo-URL</label>
              <input style={inputStyle} value={logo} onChange={(e) => setLogo(e.target.value)} placeholder="https://…/logo.png" />
              {logo.trim() ? (
                <div style={{ marginTop: 10, padding: 10, border: `1px solid ${C.bdr}`, borderRadius: 9, background: C.bg, display: "inline-block" }}>
                  <img src={logo.trim()} alt="logo voorbeeld" style={{ maxHeight: 48, maxWidth: 180, objectFit: "contain", display: "block" }} />
                </div>
              ) : null}

              <label style={{ display: "block", fontSize: 12, color: C.txt2, fontWeight: 600, margin: "14px 0 5px" }}>Plaats <span style={{ color: C.txt3, fontWeight: 400 }}>· voor het actuele weer op de beginpagina</span></label>
              <input style={{ ...inputStyle, maxWidth: 280 }} value={plaats} onChange={(e) => setPlaats(e.target.value)} placeholder="Bijv. IJmuiden" />

              <div style={{ marginTop: 18, display: "flex", alignItems: "center", gap: 12 }}>
                <button onClick={bewaarBedrijf} disabled={bedrijfStatus === "bezig"} style={knopStyle(bedrijfStatus === "bezig")}>
                  {bedrijfStatus === "bezig" ? "Bezig…" : "Opslaan"}
                </button>
                {bedrijfStatus === "klaar" && <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: C.green, fontSize: 13, fontWeight: 600 }}><Check size={15} /> Opgeslagen</span>}
              </div>
            </div>
          </Kaart>

          {/* ── Houdbaarheid (THT) ── */}
          <Kaart icon={<CalendarClock size={18} color={C.primary} />} titel="Houdbaarheid (THT)"
            omschrijving="De wettelijke bewaartermijn voor zelfbereide producten. Wordt automatisch ingevuld bij nieuwe recepten en mep-items; overschrijden vraagt om bevestiging.">
            <div style={{ marginLeft: 46 }}>
              <label style={{ display: "block", fontSize: 12, color: C.txt2, fontWeight: 600, marginBottom: 5 }}>Maximale houdbaarheid (dagen)</label>
              <input
                type="number" min="1" max="60"
                style={{ ...inputStyle, maxWidth: 140 }}
                value={thtNorm}
                onChange={(e) => setThtNorm(e.target.value)}
                placeholder="3"
              />
              <div style={{ marginTop: 12, fontSize: 12.5, color: C.txt2, lineHeight: 1.6, background: C.bg, border: `1px solid ${C.bdr}`, borderRadius: 9, padding: "10px 13px" }}>
                <strong style={{ color: C.txt }}>Norm volgens de Hygiënecode:</strong> zelfbereide producten max. <strong>2 dagen</strong> bij koeling ≤ 7 °C, of <strong>3 dagen</strong> bij ≤ 4 °C.
                Langer mag alleen met onderbouwing (nagepasteuriseerd, aangezuurd of sous-vide). Staat er een THT op het etiket van een ingrediënt, dan is die leidend.
              </div>
              <div style={{ marginTop: 18, display: "flex", alignItems: "center", gap: 12 }}>
                <button onClick={bewaarTht} disabled={thtStatus === "bezig"} style={knopStyle(thtStatus === "bezig")}>
                  {thtStatus === "bezig" ? "Bezig…" : "Opslaan"}
                </button>
                {thtStatus === "klaar" && <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: C.green, fontSize: 13, fontWeight: 600 }}><Check size={15} /> Opgeslagen</span>}
              </div>
            </div>
          </Kaart>

          {/* ── Back-up ── */}
          <Kaart icon={<Download size={18} color={C.primary} />} titel="Back-up (export)"
            omschrijving="Download een kopie van je gegevens als JSON-bestand op dit apparaat. Kies welke onderdelen je wilt meenemen.">
            <div style={{ marginLeft: 46 }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
                {EXPORT_MODULES.map((m) => {
                  const aan = !!gekozen[m.id];
                  return (
                    <button key={m.id} type="button" onClick={() => setGekozen((g) => ({ ...g, [m.id]: !g[m.id] }))}
                      style={{ display: "inline-flex", alignItems: "center", gap: 7, minHeight: 42, padding: "8px 14px", borderRadius: 10, cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: aan ? 700 : 500, border: aan ? `2px solid ${C.primary}` : `1px solid ${C.bdr}`, background: aan ? C.primBg : C.bg2, color: aan ? C.primary : C.txt }}>
                      <span style={{ width: 18, height: 18, borderRadius: 5, border: aan ? "none" : `1px solid ${C.bdr2}`, background: aan ? C.primary : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        {aan && <Check size={13} color="#fff" />}
                      </span>
                      {m.label}
                    </button>
                  );
                })}
              </div>
              <button onClick={exporteer} disabled={!ietsGekozen || bezigExport} style={knopStyle(!ietsGekozen || bezigExport)}>
                <Download size={16} /> {bezigExport ? "Exporteren…" : "Exporteren"}
              </button>
              <p style={{ fontSize: 12, color: C.txt3, margin: "14px 0 0" }}>
                Het bestand wordt lokaal op dit apparaat opgeslagen. Bewaar het op een veilige plek.
              </p>

              {/* Terugzetten / import */}
              <div style={{ borderTop: `1px solid ${C.bdr}`, marginTop: 20, paddingTop: 18 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.txt, marginBottom: 4 }}>Terugzetten (import)</div>
                <p style={{ fontSize: 13, color: C.txt2, margin: "0 0 14px" }}>
                  Zet een eerder gemaakt back-upbestand terug. <strong>Let op:</strong> dit overschrijft je huidige gegevens.
                </p>

                {!importBestand && (
                  <label style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 18px", borderRadius: 9, background: C.bg2, color: C.primary, border: `1.5px solid ${C.primBd}`, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                    <Upload size={16} /> Kies back-upbestand…
                    <input type="file" accept="application/json,.json" onChange={kiesBestand} style={{ display: "none" }} />
                  </label>
                )}

                {importFout && (
                  <div style={{ marginTop: 12, padding: "10px 12px", background: C.redBg, border: `1px solid ${C.redBd}`, borderRadius: 9, fontSize: 13, color: C.red, fontWeight: 600 }}>
                    {importFout}
                  </div>
                )}

                {importKlaar && (
                  <div style={{ marginTop: 12, padding: "10px 12px", background: C.greenBg, border: `1px solid ${C.green}`, borderRadius: 9, fontSize: 13, color: C.green, fontWeight: 600 }}>
                    {importKlaar}
                  </div>
                )}

                {importBestand && (
                  <div style={{ marginTop: 12, border: `1.5px solid ${C.redBd}`, borderRadius: 12, overflow: "hidden" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, background: C.redBg, padding: "10px 14px", color: C.red, fontWeight: 700, fontSize: 13 }}>
                      <AlertTriangle size={16} /> Gegevens worden overschreven
                    </div>
                    <div style={{ padding: 14, fontSize: 13, color: C.txt2, lineHeight: 1.6 }}>
                      <div>Bestand: <strong style={{ color: C.txt }}>{importBestand.naam}</strong></div>
                      {importBestand.data.bedrijfsnaam && <div>Van bedrijf: <strong style={{ color: C.txt }}>{importBestand.data.bedrijfsnaam}</strong></div>}
                      {importBestand.data.geexporteerd_op && <div>Gemaakt op: {new Date(importBestand.data.geexporteerd_op).toLocaleString("nl-NL")}</div>}
                      <div style={{ marginTop: 8 }}>
                        Wordt vervangen: <strong style={{ color: C.txt }}>{importBestand.moduleIds.map((id) => MODULE_LABEL[id]).join(" · ")}</strong>
                      </div>
                      {importBestand.data.tenant_id && importBestand.data.tenant_id !== tenantId && (
                        <div style={{ marginTop: 8, color: C.red, fontWeight: 600 }}>
                          Let op: deze back-up komt van een ander bedrijf. De gegevens worden in dít bedrijf geladen.
                        </div>
                      )}
                      <div style={{ marginTop: 10, color: C.txt3 }}>
                        Vóór het terugzetten downloadt de app automatisch een verse back-up van je huidige gegevens.
                      </div>
                      <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
                        <button onClick={voerImportUit} disabled={bezigImport}
                          style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 18px", borderRadius: 9, border: "none", background: bezigImport ? C.bdr : C.red, color: bezigImport ? C.txt3 : "#fff", fontSize: 14, fontWeight: 700, fontFamily: "inherit", cursor: bezigImport ? "not-allowed" : "pointer" }}>
                          {bezigImport ? "Bezig met terugzetten…" : "Ja, terugzetten"}
                        </button>
                        <button onClick={() => { setImportBestand(null); setImportFout(""); }} disabled={bezigImport}
                          style={{ padding: "10px 18px", borderRadius: 9, border: `1px solid ${C.bdr2}`, background: C.bg2, color: C.txt2, fontSize: 14, fontWeight: 600, fontFamily: "inherit", cursor: bezigImport ? "not-allowed" : "pointer" }}>
                          Annuleren
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Kaart>

          {/* ── Privacy & disclaimer ── */}
          <Kaart icon={<ShieldAlert size={18} color={C.primary} />} titel="Voorwaarden & privacy"
            omschrijving="De algemene voorwaarden en het privacybeleid van KitchenControl. Hier altijd terug te vinden.">
            <div style={{ marginLeft: 46 }}>
              <button onClick={() => setJuridischOpen("voorwaarden")}
                style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left", background: C.bg2, border: `1.5px solid ${C.primBd}`, borderRadius: 11, padding: "14px 16px", cursor: "pointer", fontFamily: "inherit", marginBottom: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 9, background: C.primBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <ShieldAlert size={18} color={C.primary} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.txt }}>Algemene voorwaarden & disclaimer</div>
                  <div style={{ fontSize: 12.5, color: C.txt2 }}>De volledige gebruiksvoorwaarden lezen</div>
                </div>
                <span style={{ color: C.txt3, fontSize: 18 }}>›</span>
              </button>

              <button onClick={() => setJuridischOpen("privacy")}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 12.5, color: C.txt2, padding: "4px 0", textDecoration: "underline" }}>
                Privacyverklaring & verwerkersovereenkomst
              </button>
            </div>
          </Kaart>
          </>)}

        </div>
      </div>

      <JuridischModal tonen={juridischOpen} onClose={() => setJuridischOpen(null)} />
    </div>
  );
}
